import type { SuiClient } from '@mysten/sui/client';
import { Aftermath, Pool as AftermathPool } from 'aftermath-ts-sdk';
import { Network, TurbosSdk } from 'turbos-clmm-sdk';

import type { CoinInRecord, TurbosPoolItem } from './types';

export class PoolManager {
  private readonly aftermathInstance: Aftermath;
  private aftermathInitialized: boolean = false;
  private readonly turbosInstance: TurbosSdk;
  private readonly client: SuiClient;
  private readonly TURBOS_POSITION =
    '0x91bfbc386a41afcfd9b2533058d7e915a1d3829089cc268ff4333d54d6339ca1::position_nft::TurbosPositionNFT';

  readonly DefaultAftermathPools: string[] = [
    '0x97aae7a80abb29c9feabbe7075028550230401ffe7fb745757d3c28a30437408',
    '0xfe25614a57b5979cfa37fc4f9d269b0aae16ba25718b82f72dcfe7b123e45607',
    '0xdeacf7ab460385d4bcb567f183f916367f7d43666a2c72323013822eb3c57026',
    '0xfd3f72a5285b0fe46be4930bde4fa4746505b7b7783ddfc56fea640efae7a905',
    '0xab995e64af6c85423a59c53bf70214e40527e7653115c70f690efd8ac8a5288b',
    '0xb0cc4ce941a6c6ac0ca6d8e6875ae5d86edbec392c3333d008ca88f377e5e181',
    '0x41ef59cccc07e3422238cbd904de574610354e49e2e0b2193b461c6c25d50b35',
  ];
  readonly DefaultTurbosPools: string[] = [
    '0x770010854059edf1dd3d49a97f3054c39b870ec708fe2f408e30a8ef4724caef',
    '0x77f786e7bbd5f93f7dc09edbcffd9ea073945564767b65cf605f388328449d50',
    '0x2c6fc12bf0d093b5391e7c0fed7e044d52bc14eb29f6352a3fb358e33e80729e',
    '0x27645957e0260f3c5874c4895c11d2adca6b2c3d60ad4afb805acb635dd46f21',
    '0x21026a3198bf7e656441355dd78f2c14778134d10d16d17c2cf5651d99d00ee9',
    '0xa0b55c090a286dfc77125231735077bc12a7bf865feb07accdac6f5be03f69b4',
    '0x6a3be30a31f88d9055da7f26f53dd34c85bc5aab9028212361ccf67f5f00fd46',
    '0xbd85f61a1b755b6034c62f16938d6da7c85941705d9d10aa1843b809b0e35582',
    '0xe432a72c0921c8d75160eba6da7723fd7f7e1a74342d12e9fdd42d975713b51f',
    '0x743ec55da4c6429a0d9a84e142350f52a87e599bcc36becc135c580113a2cdab',
    '0x4c5bd926a3864b0cca1515aef81170e49c2eaae9c24e5517189b24a2939f4a8f',
    '0x5d84ea40c75d3b592d996ce1c5418916576f168ea26dd99dbb0b1cfd55a7c896',
  ];

  constructor(client: SuiClient) {
    this.aftermathInstance = new Aftermath('MAINNET');
    this.turbosInstance = new TurbosSdk(Network.mainnet, {
      url: 'https://fullnode-doubleup.com',
    });
    this.client = client;
  }

  async getTurbosPositions(pools: TurbosPoolItem[], address: string) {
    let cursor: string | undefined | null = undefined;
    let hasNextPage = true;
    const positions: string[] = [];

    while (hasNextPage) {
      const {
        data,
        hasNextPage: nextHasNextPage,
        nextCursor,
      } = await this.client.getOwnedObjects({
        owner: address,
        filter: {
          StructType: this.TURBOS_POSITION,
        },
        cursor,
        limit: 20,
      });

      positions.push(
        ...data
          .filter((obj) => obj.data)
          .map((obj) => obj.data?.objectId as string)
      );
      cursor = nextCursor;
      hasNextPage = nextHasNextPage;
    }

    const poolIds = pools.map((pool) => pool.pool_id);

    const parsedPositions = await Promise.all(
      positions.map((objectId) =>
        this.getTurbosPositionNftFields(objectId, this.turbosInstance)
      )
    );

    return parsedPositions.filter((position) =>
      poolIds.includes(position.nft.pool_id)
    );
  }

  async getAftermathPositions(
    pools: AftermathPool[],
    coinInRecord: CoinInRecord
  ) {
    const coinTypes = pools.map((pool) => pool.pool.lpCoinType);

    const existingCoins = Object.keys(coinInRecord).filter((coin) =>
      coinTypes.includes(coin)
    );

    return existingCoins;
  }

  async getPools() {
    await this.initializeAftermath();

    return {
      aftermath: await this.getAftermathPools(),
      turbos: await this.getTurbosPools(),
    };
  }

  async getAftermathPools() {
    await this.initializeAftermath();
    return await this.aftermathInstance.Pools().getAllPools();
  }

  async getSelectiveAftermathPools(
    pools: string[] = this.DefaultAftermathPools
  ) {
    await this.initializeAftermath();
    return await this.aftermathInstance.Pools().getPools({
      objectIds: pools,
    });
  }

  async getTurbosPools() {
    return this.turbosInstance.pool.getPools();
  }

  async getSelectiveTurbosPools(
    pools: string[] = this.DefaultTurbosPools
  ): Promise<TurbosPoolItem[]> {
    const result: {
      code: number;
      data: TurbosPoolItem[];
      message: string;
    } = await fetch(
      'https://api.turbos.finance/pools/ids?' +
        new URLSearchParams({
          ids: pools.join(','),
          returnTicks: 'false',
        })
    ).then(
      (res) =>
        res.json() as Promise<{
          code: number;
          data: TurbosPoolItem[];
          message: string;
        }>
    );

    if (result.message !== 'OK') {
      throw new Error(result.message);
    }

    return result.data;
  }

  private async getTurbosPositionNftFields(
    objectId: string,
    instance: TurbosSdk
  ) {
    const nftFields = await instance.nft.getFields(objectId);
    const positionFields = await instance.nft.getPositionFields(objectId);

    return {
      objectId,
      nft: nftFields,
      position: positionFields,
    };
  }

  private async initializeAftermath() {
    if (this.aftermathInitialized) {
      return;
    }

    this.aftermathInitialized = true;
    await this.aftermathInstance.init();
  }
}
